type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, any>;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;
  private rotationSize: number = 5 * 1024 * 1024; // 5MB
  private archiveLimit: number = 10;
  private archives: LogEntry[][] = [];

  private constructor() {
    this.setupRotation();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private setupRotation() {
    setInterval(() => {
      this.checkRotation();
    }, 60000); // Check every minute
  }

  private checkRotation() {
    const logsSize = new TextEncoder().encode(JSON.stringify(this.logs)).length;
    
    if (logsSize > this.rotationSize) {
      this.rotate();
    }
  }

  private rotate() {
    // Archive current logs
    this.archives.unshift([...this.logs]);
    
    // Clear current logs
    this.logs = [];
    
    // Keep only the latest archives
    if (this.archives.length > this.archiveLimit) {
      this.archives = this.archives.slice(0, this.archiveLimit);
    }
  }

  private log(level: LogLevel, message: string, context?: Record<string, any>) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
    };

    this.logs.push(entry);
    this.checkRotation();

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console[level](message, context);
    }
  }

  debug(message: string, context?: Record<string, any>) {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, any>) {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, any>) {
    this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, any>) {
    this.log('error', message, context);
  }

  getLogs(level?: LogLevel, includeArchives: boolean = false): LogEntry[] {
    let result = level 
      ? this.logs.filter(log => log.level === level)
      : this.logs;

    if (includeArchives) {
      const archivedLogs = this.archives.flat();
      result = result.concat(
        level 
          ? archivedLogs.filter(log => log.level === level)
          : archivedLogs
      );
    }

    return result;
  }

  getArchives(): LogEntry[][] {
    return this.archives;
  }

  clearLogs() {
    this.logs = [];
  }

  clearArchives() {
    this.archives = [];
  }

  setMaxLogs(max: number) {
    this.maxLogs = max;
  }

  setRotationSize(size: number) {
    this.rotationSize = size;
  }

  setArchiveLimit(limit: number) {
    this.archiveLimit = limit;
    if (this.archives.length > limit) {
      this.archives = this.archives.slice(0, limit);
    }
  }

  getStats() {
    return {
      currentLogs: this.logs.length,
      archives: this.archives.length,
      totalEntries: this.logs.length + this.archives.reduce((sum, archive) => sum + archive.length, 0),
      byLevel: {
        debug: this.logs.filter(log => log.level === 'debug').length,
        info: this.logs.filter(log => log.level === 'info').length,
        warn: this.logs.filter(log => log.level === 'warn').length,
        error: this.logs.filter(log => log.level === 'error').length,
      }
    };
  }
}

export const logger = Logger.getInstance();